import React, { useState, useEffect, useMemo } from 'react';
import { Container, Card, Table, Button, Modal, Form as BootstrapForm, Row as BootstrapRow, Col as BootstrapCol } from 'react-bootstrap';
import PageTitle from '@/common/PageTitle/PageTitle';
import { Form, Spin } from 'antd';
import { FilterOutlined, LoadingOutlined } from '@ant-design/icons';

import FormItem from '@/common/FormItem/FormItem';
import DateRangePicker from '@/common/DateRangePicker/DateRangePicker';
import { Select } from 'antd';
import dayjs from 'dayjs';
import { withAuthenticationRequired } from '@auth0/auth0-react';
import { PlusOutlined, EyeOutlined, EditOutlined, DeleteOutlined, PlayCircleOutlined } from '@ant-design/icons';
import { Tooltip } from 'antd';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { DEFAULT_ALBUM_ART } from '@/utils/artwork';
import { EntityRefBadge } from '@/components/EntityRef/EntityRefBadge';
import styles from './channel-admin.module.scss';

import type { Channel as ApiChannel, ChannelMusicDirector } from '@/features/channels/types';
import {
  useListChannelsQuery,
  useCreateChannelMutation,
  useUpdateChannelMutation,
  useDeleteChannelMutation,
  useSetChannelMusicDirectorsMutation,
  extractChannelErrorMessage,
} from '@/features/channels/channelsApi';
import {
  toUiStatus,
  fromUiStatus,
  formatChannelUpdatedAt,
  type UiChannelStatus,
} from '@/features/channels/channelStatus';
import { useListEmployeesQuery } from '@/features/employees/employeesApi';
import type { ApiEmployee } from '@/features/employees/employeeTypes';

interface NewChannelForm {
  name: string;
  type: string;
  status: string;
  musicDirectors: string[]; // userSubs
  order: number | string;
}

interface Channel {
  id: string;
  name: string;
  description: string;
  order: number;
  type: string;
  status: UiChannelStatus;
  musicDirectors: string;
  musicDirectorsList: ChannelMusicDirector[];
  lastUpdated: string;
  liveSongs: number;
  liveRotations: number;
  songs: number;
  rotations: number;
  artwork: string;
  featuredArtists: string;
  genres: string;
}

const statusColors: Record<string, string> = {
  'LIVE': '#e21e26',
  'NOT LIVE': '#6c757d',
  'APPROVED': '#f7b731',
  'PENDING': '#007bff',
  'DISABLED': '#6c757d',
};

/** Must stay aligned with admin-api MIN_LIVE_PRODUCTION_SONGS. */
const MIN_LIVE_PRODUCTION_SONGS = 50;

const channelStatusOptions = [
  { label: "Live", value: "LIVE" },
  { label: "Not Live", value: "NOT LIVE" },
  { label: "Approved", value: "APPROVED" },
  { label: "Pending", value: "PENDING" }
];

const channelTypeOptions = [
  { label: "KK Original", value: "KK Original" },
  { label: "Commercial", value: "Commercial" }
];

/** Normalize legacy "Exclusive" channel-type labels to "Original" for display/filters. */
function displayChannelType(type: string): string {
  return (type || '').replace(/Exclusive/gi, 'Original');
}

function isChannelTypeMatch(channelType: string, filterValue: string): boolean {
  return displayChannelType(channelType).toLowerCase() === displayChannelType(filterValue).toLowerCase();
}

function employeeDisplayName(emp: ApiEmployee): string {
  if (emp.displayName && emp.displayName.trim()) return emp.displayName.trim();
  const parts = [emp.firstName, emp.lastName].filter(Boolean);
  if (parts.length) return parts.join(' ');
  return emp.emailAddress || emp.auth0Sub || 'Unknown';
}

function isMusicDirectorLike(emp: ApiEmployee): boolean {
  const key = (emp.employeeRoleKey || '').toLowerCase();
  const display = (emp.roleDisplayName || '').toLowerCase();
  return (
    key === 'music-director' ||
    key.includes('music-director') ||
    display.includes('music director')
  );
}

function isActiveEmployee(emp: ApiEmployee): boolean {
  const status = (emp.status || '').toLowerCase();
  return status === 'active' || status === '';
}

function toChannelRow(ch: ApiChannel): Channel {
  const directors = ch.musicDirectors || [];
  return {
    id: ch.channelId,
    name: ch.name,
    description: ch.description || '',
    order: ch.order,
    type: displayChannelType(ch.channelType || ''),
    status: toUiStatus(ch.status),
    musicDirectors: directors.map((md) => md.name).join(', '),
    musicDirectorsList: directors,
    lastUpdated: formatChannelUpdatedAt(ch.updatedAt),
    liveSongs: ch.productionSongCount ?? 0,
    liveRotations: ch.productionRotationCount ?? 0,
    songs: ch.stagingSongCount ?? 0,
    rotations: ch.stagingRotationCount ?? 0,
    artwork: ch.artworkUrl || DEFAULT_ALBUM_ART,
    featuredArtists: ch.featuredArtists || '',
    genres: ch.genres || '',
  };
}

const ChannelAdmin: React.FC = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();

  const { data: channelsData, isLoading, isFetching, error: listError } = useListChannelsQuery();
  const { data: employeesData } = useListEmployeesQuery();
  const [createChannel, createState] = useCreateChannelMutation();
  const [updateChannel, updateState] = useUpdateChannelMutation();
  const [deleteChannel, deleteState] = useDeleteChannelMutation();
  const [setMusicDirectors, setMdState] = useSetChannelMusicDirectorsMutation();

  const channels = useMemo(
    () => (channelsData?.items || []).map(toChannelRow).sort((a, b) => a.order - b.order),
    [channelsData],
  );

  const musicDirectorCandidates = useMemo(() => {
    const employees = (employeesData?.employees || []).filter(isActiveEmployee);
    const mdLike = employees.filter(isMusicDirectorLike);
    const source = mdLike.length > 0 ? mdLike : employees;
    return source
      .filter((emp) => !!emp.auth0Sub)
      .map((emp) => ({
        id: emp.auth0Sub as string,
        name: employeeDisplayName(emp),
        email: emp.emailAddress || '',
        department: emp.departmentDisplayName || emp.department || '',
        role: emp.roleDisplayName || emp.employeeRoleKey || '',
      }));
  }, [employeesData]);

  const musicDirectorOptions = useMemo(
    () =>
      musicDirectorCandidates.map((director) => ({
        label: director.name,
        value: director.name,
      })),
    [musicDirectorCandidates],
  );

  const topRowSelectInputs = [
    { label: "Channel", name: "channels", placeholder: "Any channel", extraData: [] as { label: string; value: string }[] },
    { label: "Channel Status", name: "channel_status", placeholder: "Any status", extraData: channelStatusOptions },
    { label: "Channel Type", name: "channel_type", placeholder: "Any type", extraData: channelTypeOptions }
  ];

  const secondRowSelectInputs = [
    { label: "Music Directors", name: "music_directors", placeholder: "Any music director", extraData: musicDirectorOptions }
  ];

  const [filteredChannels, setFilteredChannels] = useState<Channel[]>([]);
  const [dateFilter, setDateFilter] = useState<{ startDate: string | null; endDate: string | null }>({ startDate: null, endDate: null });
  const [resetCounter, setResetCounter] = useState(0);
  const [filters, setFilters] = useState({
    channels: undefined as string | undefined,
    channel_status: undefined as string | undefined,
    channel_type: undefined as string | undefined,
    music_directors: undefined as string | undefined,
  });
  const [showAddModal, setShowAddModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedChannel, setSelectedChannel] = useState<Channel | null>(null);
  const [editingChannel, setEditingChannel] = useState<Channel | null>(null);
  const [editingDirectorSubs, setEditingDirectorSubs] = useState<string[]>([]);
  const [newChannel, setNewChannel] = useState<NewChannelForm>({
    name: '',
    type: 'KK Original',
    status: 'PENDING',
    musicDirectors: [],
    order: 1
  });

  useEffect(() => {
    if (listError) {
      toast.error(extractChannelErrorMessage(listError));
    }
  }, [listError]);

  useEffect(() => {
    setNewChannel((prev) => ({
      ...prev,
      order: channels.length + 1,
    }));
  }, [channels.length]);

  const directorsBySub = useMemo(() => {
    const map = new Map<string, { userSub: string; name: string; role: string }>();
    for (const d of musicDirectorCandidates) {
      map.set(d.id, { userSub: d.id, name: d.name, role: d.role || 'Music Director' });
    }
    return map;
  }, [musicDirectorCandidates]);

  const resolveDirectorsPayload = (subs: string[]) =>
    subs
      .map((sub) => directorsBySub.get(sub))
      .filter((d): d is { userSub: string; name: string; role: string } => !!d);

  const handleAddChannel = async () => {
    const orderValue = typeof newChannel.order === 'string' ? parseInt(newChannel.order) : newChannel.order;
    if (!newChannel.order || newChannel.order === '' || isNaN(orderValue) || orderValue < 1) {
      alert('Please enter a valid order number (must be 1 or greater).');
      return;
    }

    const existingChannel = channels.find(channel => channel.order === orderValue);
    if (existingChannel) {
      alert(`Error: Order number ${orderValue} is already taken by "${existingChannel.name}". Please choose a different order number.`);
      const maxOrder = Math.max(...channels.map(c => c.order), 0);
      setNewChannel(prev => ({ ...prev, order: maxOrder + 1 }));
      return;
    }

    try {
      await createChannel({
        name: newChannel.name.trim(),
        channelType: newChannel.type,
        status: fromUiStatus(newChannel.status),
        order: orderValue,
        musicDirectors: resolveDirectorsPayload(newChannel.musicDirectors),
      }).unwrap();
      toast.success(`Created channel "${newChannel.name.trim()}"`);
      setShowAddModal(false);
      setNewChannel({
        name: '',
        type: 'KK Original',
        status: 'PENDING',
        musicDirectors: [],
        order: channels.length + 1
      });
    } catch (err) {
      toast.error(extractChannelErrorMessage(err));
    }
  };

  const handleInputChange = (field: keyof NewChannelForm, value: string | number | string[]) => {
    setNewChannel(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleViewChannel = (channel: Channel) => {
    setSelectedChannel(channel);
    setShowViewModal(true);
  };

  const handleEditChannel = (channel: Channel) => {
    setEditingChannel({ ...channel });
    setEditingDirectorSubs(channel.musicDirectorsList.map((md) => md.userSub));
    setShowEditModal(true);
  };

  const handleUpdateChannel = async () => {
    if (!editingChannel) return;

    const orderValue = typeof editingChannel.order === 'string' ? parseInt(String(editingChannel.order)) : editingChannel.order;

    if (!editingChannel.order || (typeof editingChannel.order === 'string' && editingChannel.order === '') || isNaN(orderValue) || orderValue < 1) {
      alert('Please enter a valid order number (must be 1 or greater).');
      return;
    }

    const existingChannel = channels.find(channel =>
      channel.order === orderValue && channel.id !== editingChannel.id
    );
    if (existingChannel) {
      alert(`Error: Order number ${orderValue} is already taken by "${existingChannel.name}". Please choose a different order number.`);
      return;
    }

    try {
      // Only PATCH fields that actually changed — the API rejects no-op bodies.
      const original = channels.find((c) => c.id === editingChannel.id);
      const patch: {
        channelId: string;
        name?: string;
        channelType?: string;
        status?: ReturnType<typeof fromUiStatus>;
        order?: number;
      } = { channelId: editingChannel.id };
      const nextName = editingChannel.name.trim();
      if (!original || nextName !== original.name) patch.name = nextName;
      if (!original || editingChannel.type !== original.type) patch.channelType = editingChannel.type;
      if (!original || editingChannel.status !== original.status) patch.status = fromUiStatus(editingChannel.status);
      if (!original || orderValue !== original.order) patch.order = orderValue;

      if (Object.keys(patch).length > 1) {
        await updateChannel(patch).unwrap();
      }

      const originalSubs = (original?.musicDirectorsList || []).map((md) => md.userSub);
      const directorsChanged =
        originalSubs.length !== editingDirectorSubs.length ||
        editingDirectorSubs.some((sub) => !originalSubs.includes(sub));
      if (!original || directorsChanged) {
        await setMusicDirectors({
          channelId: editingChannel.id,
          musicDirectors: resolveDirectorsPayload(editingDirectorSubs),
        }).unwrap();
      }

      toast.success(`Updated channel "${nextName}"`);
      setShowEditModal(false);
      setEditingChannel(null);
      setEditingDirectorSubs([]);
    } catch (err) {
      toast.error(extractChannelErrorMessage(err));
    }
  };

  const handleDeleteChannel = async (channelId: string) => {
    if (!window.confirm('Are you sure you want to delete this channel?')) return;
    try {
      await deleteChannel({ channelId }).unwrap();
      toast.success('Channel deleted');
      if (editingChannel && editingChannel.id === channelId) {
        setShowEditModal(false);
        setEditingChannel(null);
      }
      if (selectedChannel && selectedChannel.id === channelId) {
        setShowViewModal(false);
        setSelectedChannel(null);
      }
    } catch (err) {
      toast.error(extractChannelErrorMessage(err));
    }
  };

  const handleEditInputChange = (field: keyof Channel, value: string | number) => {
    if (editingChannel) {
      setEditingChannel(prev => prev ? { ...prev, [field]: value } : null);
    }
  };

  const handleMusicDirectorsChange = (selectedSubs: string[]) => {
    setEditingDirectorSubs(selectedSubs);
    const names = selectedSubs
      .map((sub) => directorsBySub.get(sub)?.name)
      .filter((n): n is string => !!n);
    if (editingChannel) {
      setEditingChannel(prev => prev ? {
        ...prev,
        musicDirectors: names.join(', ')
      } : null);
    }
  };

  const parseChannelDate = (dateString: string): Date => {
    const [month, day, year] = dateString.split('/');
    const fullYear = parseInt(year) + 2000;
    return new Date(fullYear, parseInt(month) - 1, parseInt(day));
  };

  const handleDateChange = (dates: any) => {
    if (dates) {
      const [startDate, endDate] = dates;
      const startDateStr = startDate.utc().format("YYYY-MM-DD");
      const endDateStr = endDate.utc().format("YYYY-MM-DD");

      form.setFieldsValue({
        startDate: startDateStr,
        endDate: endDateStr,
      });

      setDateFilter({ startDate: startDateStr, endDate: endDateStr });
    } else {
      form.setFieldsValue({
        startDate: null,
        endDate: null,
      });
      setDateFilter({ startDate: null, endDate: null });
    }
  };

  const applyFilters = () => {
    let filtered = [...channels];

    if (dateFilter.startDate && dateFilter.endDate) {
      filtered = filtered.filter((channel) => {
        const channelDate = parseChannelDate(channel.lastUpdated);
        const filterStartDate = new Date(dateFilter.startDate!);
        const filterEndDate = new Date(dateFilter.endDate!);
        return channelDate >= filterStartDate && channelDate <= filterEndDate;
      });
    }

    if (filters.channels) {
      filtered = filtered.filter(channel =>
        channel.name.toLowerCase().includes(filters.channels!.toLowerCase())
      );
    }

    if (filters.channel_status) {
      filtered = filtered.filter(channel => channel.status === filters.channel_status);
    }

    if (filters.channel_type) {
      filtered = filtered.filter(channel => isChannelTypeMatch(channel.type, filters.channel_type!));
    }

    if (filters.music_directors) {
      filtered = filtered.filter(channel =>
        channel.musicDirectors.includes(filters.music_directors!)
      );
    }

    setFilteredChannels(filtered);
  };

  const handleOnSelectFilterOption = (value: any, _option: any, name: string) => {
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleOnClearInput = (name: string) => {
    setFilters(prev => ({
      ...prev,
      [name]: undefined
    }));
  };

  const handleReset = () => {
    setDateFilter({ startDate: null, endDate: null });
    setFilters({
      channels: undefined,
      channel_status: undefined,
      channel_type: undefined,
      music_directors: undefined,
    });
    setFilteredChannels(channels);
    form.resetFields();
    setResetCounter(prev => prev + 1);
  };

  useEffect(() => {
    applyFilters();
  }, [filters, dateFilter, channels]);

  const saving =
    createState.isLoading ||
    updateState.isLoading ||
    deleteState.isLoading ||
    setMdState.isLoading;

  return (
    <Container fluid>
      <Card>
        <Card.Body>
          <Card.Title style={{ display: 'flex', justifyContent: 'space-between' }}>
            <PageTitle title="Channel Admin" />
            <Button
              onClick={handleReset}
              style={{
                background: 'white',
                color: '#333',
                border: '1px solid #e0e0e0',
                borderRadius: '6px',
                padding: '8px 16px',
                fontWeight: '500'
              }}
            >
              <FilterOutlined /> Reset All
            </Button>
          </Card.Title>
          <Form layout="vertical" style={{ marginBottom: 24 }} form={form}>
            <BootstrapRow>
              <BootstrapCol>
                <BootstrapRow className="mb-2">
                  <BootstrapCol>
                    <BootstrapRow>
                      {topRowSelectInputs.map(({ name, label, placeholder, extraData }) => (
                        <BootstrapCol key={name} md={4}>
                          <FormItem
                            label={label}
                            className="customSelectWrapper"
                            layout="vertical"
                          >
                            <Select
                              key={`${name}-reset-${resetCounter}`}
                              showSearch
                              allowClear
                              className="customSelect"
                              placeholder={placeholder}
                              style={{ width: "100%" }}
                              value={filters[name as keyof typeof filters]}
                              options={name === "channels" ? channels.map(ch => ({ label: ch.name, value: ch.name })) : extraData}
                              onSelect={(value: any, option: any) => handleOnSelectFilterOption(value, option, name)}
                              onClear={() => handleOnClearInput(name)}
                            />
                          </FormItem>
                        </BootstrapCol>
                      ))}
                    </BootstrapRow>
                  </BootstrapCol>
                </BootstrapRow>
                <BootstrapRow className="mb-2">
                  <BootstrapCol>
                    <BootstrapRow>
                      {secondRowSelectInputs.map(({ name, label, placeholder, extraData }) => (
                        <BootstrapCol key={name} md={4}>
                          <FormItem
                            label={label}
                            className="customSelectWrapper"
                            layout="vertical"
                          >
                            <Select
                              key={`${name}-reset-${resetCounter}`}
                              showSearch
                              allowClear
                              className="customSelect"
                              placeholder={placeholder}
                              style={{ width: "100%" }}
                              value={filters[name as keyof typeof filters]}
                              options={extraData}
                              onSelect={(value: any, option: any) => handleOnSelectFilterOption(value, option, name)}
                              onClear={() => handleOnClearInput(name)}
                            />
                          </FormItem>
                        </BootstrapCol>
                      ))}
                    </BootstrapRow>
                  </BootstrapCol>
                </BootstrapRow>
                <BootstrapRow className="mb-2">
                  <BootstrapCol md={8}>
                    <FormItem
                      label="Last Updated"
                      layout="vertical"
                    >
                      <DateRangePicker
                        key={`dateRange-reset-${resetCounter}`}
                        maxDate={dayjs()}
                        onChange={handleDateChange}
                        style={{ width: '100%' }}
                      />
                    </FormItem>
                  </BootstrapCol>
                  <BootstrapCol
                    md={4}
                    className="d-flex align-items-center mt-2 mt-md-0"
                  >
                    <Button
                      className="w-100"
                      style={{
                        background: 'white',
                        color: '#e21e26',
                        border: '1px solid #e21e26',
                        borderRadius: '6px',
                        padding: '8px 16px',
                        fontWeight: '500',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        justifyContent: 'center'
                      }}
                      onClick={() => setShowAddModal(true)}
                    >
                      <PlusOutlined style={{ fontSize: 16 }} />
                      Add New Channel
                    </Button>
                  </BootstrapCol>
                </BootstrapRow>
              </BootstrapCol>
            </BootstrapRow>
          </Form>
          <Spin spinning={isLoading || isFetching || saving} indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}>
            <div className={styles.tableContainer}>
              <Table
                bordered
                responsive
                className={styles.channelAdminTable}
              >
                <thead className={styles.tableHeader}>
                  <tr>
                    <th className={styles.checkboxCol}><input type="checkbox" /></th>
                    <th className={styles.artworkCol}>Artwork</th>
                    <th className={styles.channelOrderCol}>#</th>
                    <th className={styles.channelCol}>Channel</th>
                    <th className={styles.channelTypeCol}>Channel Type</th>
                    <th className={styles.statusCol}>Status</th>
                    <th className={styles.musicDirectorsCol}>Music Directors</th>
                    <th className={styles.lastUpdatedCol}>Last Updated</th>
                    <th className={styles.actionCol}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredChannels.map((ch) => (
                    <tr key={ch.id}>
                      <td className={styles.checkboxCol}><input type="checkbox" /></td>
                      <td className={styles.artworkCol}>
                        <img src={ch.artwork} alt={ch.name} className={styles.artworkImage} />
                      </td>
                      <td className={styles.channelOrderCol}>{ch.order}</td>
                      <td className={styles.channelCol}>{ch.name}</td>
                      <td className={styles.channelTypeCol}>{ch.type}</td>
                      <td className={styles.statusCol}>
                        <span className={`${styles.statusPill} ${ch.status === 'LIVE' ? styles.statusPillLive : ''}`}>
                          {ch.status}
                          {ch.status === 'LIVE' && (
                            <span style={{ marginLeft: 6, fontSize: 13, verticalAlign: 'middle', textAlign: 'left' }}>📡</span>
                          )}
                        </span>
                      </td>
                      <td className={styles.musicDirectorsCol}>
                        {ch.musicDirectors ? ch.musicDirectors : 'No music directors assigned'}
                      </td>
                      <td className={styles.lastUpdatedCol}>{ch.lastUpdated}</td>
                      <td className={styles.actionCol}>
                        <Tooltip title="View Channel Details">
                          <button
                            onClick={() => handleViewChannel(ch)}
                            className={styles.actionButton}
                          >
                            <EyeOutlined />
                          </button>
                        </Tooltip>
                        <Tooltip title="Edit Channel">
                          <button
                            onClick={() => handleEditChannel(ch)}
                            className={styles.actionButton}
                          >
                            <EditOutlined />
                          </button>
                        </Tooltip>
                        <Tooltip title="Delete Channel">
                          <button
                            onClick={() => handleDeleteChannel(ch.id)}
                            className={styles.deleteButton}
                          >
                            <DeleteOutlined />
                          </button>
                        </Tooltip>
                        <Tooltip title="Program Channel">
                          <button
                            onClick={() => navigate(`/channel-programming/${ch.id}`)}
                            className={styles.actionButton}
                          >
                            <PlayCircleOutlined />
                          </button>
                        </Tooltip>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </Spin>
          <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginTop: 16 }}>
            <Button style={{ background: '#e0e0e0', color: '#333', border: 'none', marginRight: 8, fontWeight: 600 }}>Prev</Button>
            <span style={{ fontSize: 14, marginRight: 8 }}>Page: 1</span>
            <Button style={{ background: '#e21e26', color: 'white', border: 'none', fontWeight: 600 }}>Next</Button>
          </div>
        </Card.Body>

      </Card>

      {/* Add New Channel Modal */}
      <Modal show={showAddModal} onHide={() => {
        setShowAddModal(false);
        setNewChannel({
          name: '',
          type: 'KK Original',
          status: 'PENDING',
          musicDirectors: [],
          order: channels.length + 1
        });
      }} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Add New Channel</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <BootstrapForm>
            <BootstrapForm.Group className="mb-3">
              <BootstrapForm.Label>Channel Name *</BootstrapForm.Label>
              <BootstrapForm.Control
                type="text"
                placeholder="Enter channel name"
                value={newChannel.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                required
              />
            </BootstrapForm.Group>

            <BootstrapForm.Group className="mb-3">
              <BootstrapForm.Label>Channel Type *</BootstrapForm.Label>
              <BootstrapForm.Select
                value={newChannel.type}
                onChange={(e) => handleInputChange('type', e.target.value)}
                required
              >
                <option value="KK Original">KK Original</option>
                <option value="Commercial">Commercial</option>
              </BootstrapForm.Select>
            </BootstrapForm.Group>

            <BootstrapForm.Group className="mb-3">
              <BootstrapForm.Label>Status *</BootstrapForm.Label>
              <BootstrapForm.Select
                value={newChannel.status}
                onChange={(e) => handleInputChange('status', e.target.value)}
                required
              >
                <option value="PENDING">Pending</option>
                <option value="APPROVED">Approved</option>
                <option value="LIVE" disabled>
                  Live (Minimum: {MIN_LIVE_PRODUCTION_SONGS} Songs)
                </option>
                <option value="NOT LIVE">Not Live</option>
              </BootstrapForm.Select>
              <BootstrapForm.Text muted>
                Live requires a production schedule with at least{' '}
                {MIN_LIVE_PRODUCTION_SONGS} songs (set after programming Go Live).
              </BootstrapForm.Text>
            </BootstrapForm.Group>

              <BootstrapForm.Group className="mb-3">
                <BootstrapForm.Label>Music Directors</BootstrapForm.Label>
                <div style={{
                  border: '1px solid #ced4da',
                  borderRadius: '0.375rem',
                  padding: '12px',
                  maxHeight: '200px',
                  overflowY: 'auto',
                  backgroundColor: '#f8f9fa'
                }}>
                  {musicDirectorCandidates.map((director) => (
                      <div key={director.id} style={{ marginBottom: '8px', display: 'flex', alignItems: 'center' }}>
                        <input
                          type="checkbox"
                          id={`new-${director.id}`}
                          checked={newChannel.musicDirectors.includes(director.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              handleInputChange('musicDirectors', [...newChannel.musicDirectors, director.id]);
                            } else {
                              handleInputChange('musicDirectors', newChannel.musicDirectors.filter(id => id !== director.id));
                            }
                          }}
                          style={{ marginRight: '0px' }}
                        />
                        <label htmlFor={`new-${director.id}`} style={{ margin: 0, marginLeft: '8px', cursor: 'pointer' }}>
                          {director.name} - {director.role}
                        </label>
                      </div>
                    ))}
                </div>
                <div style={{ marginTop: 8 }}>
                  <BootstrapForm.Text className="text-muted">
                    Select one or more music directors for this channel
                  </BootstrapForm.Text>
                  <br />
                  <BootstrapForm.Text className="text-muted">
                    Leave empty if no music directors are assigned to this channel
                  </BootstrapForm.Text>
                </div>
                {newChannel.musicDirectors.length > 0 && (
                  <div style={{ marginTop: 8 }}>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => handleInputChange('musicDirectors', [])}
                      style={{ fontSize: 12, color: '#000000' }}
                    >
                      Clear Selection
                    </Button>
                  </div>
                )}
              </BootstrapForm.Group>

            <BootstrapForm.Group className="mb-3">
              <BootstrapForm.Label>Order *</BootstrapForm.Label>
              <BootstrapForm.Control
                type="text"
                placeholder="Enter channel order"
                value={newChannel.order === '' ? '' : newChannel.order}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '' || /^\d+$/.test(value)) {
                    handleInputChange('order', value);
                  }
                }}
                required
              />
            </BootstrapForm.Group>
          </BootstrapForm>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => {
            setShowAddModal(false);
            setNewChannel({
              name: '',
              type: 'KK Original',
              status: 'PENDING',
              musicDirectors: [],
              order: channels.length + 1
            });
          }}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleAddChannel}
            disabled={!newChannel.name.trim() || createState.isLoading}
            style={{ background: '#e21e26', border: 'none' }}
          >
            {createState.isLoading ? 'Creating…' : 'Create Channel'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* View Channel Modal */}
      <Modal show={showViewModal} onHide={() => setShowViewModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Channel Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedChannel && (
            <div>
              <div style={{ marginBottom: 20 }}>
                <h5 style={{ color: '#333', marginBottom: 8 }}>Channel Information</h5>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  <div>
                    <strong>Channel Name:</strong>
                    <p style={{ margin: '4px 0', color: '#666' }}>{selectedChannel.name}</p>
                  </div>
                  <div>
                    <strong>Channel Type:</strong>
                    <p style={{ margin: '4px 0', color: '#666' }}>{selectedChannel.type}</p>
                  </div>
                  <div>
                    <strong>Status:</strong>
                    <p style={{ margin: '4px 0' }}>
                      <span style={{
                        background: statusColors[selectedChannel.status] || '#e0e0e0',
                        color: selectedChannel.status === 'LIVE' ? 'white' : '#333',
                        borderRadius: 6,
                        padding: '4px 12px',
                        fontWeight: 700,
                        fontSize: 12,
                        display: 'inline-block',
                      }}>
                        {selectedChannel.status}
                        {selectedChannel.status === 'LIVE' && (
                          <span style={{ marginLeft: 6, fontSize: 14 }}>📡</span>
                        )}
                      </span>
                    </p>
                  </div>
                  <div>
                    <strong>Order:</strong>
                    <p style={{ margin: '4px 0', color: '#666' }}>{selectedChannel.order}</p>
                  </div>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <strong>Music Directors:</strong>
                    <p style={{ margin: '4px 0', color: '#666' }}>
                      {selectedChannel.musicDirectors ? selectedChannel.musicDirectors : 'No music directors assigned'}
                    </p>
                  </div>
                  <div>
                    <strong>Last Updated:</strong>
                    <p style={{ margin: '4px 0', color: '#666' }}>{selectedChannel.lastUpdated}</p>
                  </div>
                  <div>
                    <strong>Channel ID:</strong>
                    <p style={{ margin: '4px 0', color: '#666' }}>
                      <EntityRefBadge kind="channel" id={selectedChannel.id} />
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowViewModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Channel Modal */}
      <Modal show={showEditModal} onHide={() => {
        setShowEditModal(false);
        setEditingChannel(null);
        setEditingDirectorSubs([]);
      }} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Edit Channel</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editingChannel && (
            <BootstrapForm>
              <BootstrapForm.Group className="mb-3">
                <BootstrapForm.Label>Channel Name *</BootstrapForm.Label>
                <BootstrapForm.Control
                  type="text"
                  placeholder="Enter channel name"
                  value={editingChannel.name}
                  onChange={(e) => handleEditInputChange('name', e.target.value)}
                  required
                />
              </BootstrapForm.Group>

              <BootstrapForm.Group className="mb-3">
                <BootstrapForm.Label>Channel Type *</BootstrapForm.Label>
                <BootstrapForm.Select
                  value={editingChannel.type}
                  onChange={(e) => handleEditInputChange('type', e.target.value)}
                  required
                >
                  <option value="KK Original">KK Original</option>
                  <option value="Commercial">Commercial</option>
                </BootstrapForm.Select>
              </BootstrapForm.Group>

              <BootstrapForm.Group className="mb-3">
                <BootstrapForm.Label>Status *</BootstrapForm.Label>
                <BootstrapForm.Select
                  value={editingChannel.status}
                  onChange={(e) => handleEditInputChange('status', e.target.value)}
                  required
                >
                  <option value="PENDING">Pending</option>
                  <option value="APPROVED">Approved</option>
                  <option
                    value="LIVE"
                    disabled={
                      editingChannel.status !== 'LIVE' &&
                      (editingChannel.liveSongs ?? 0) < MIN_LIVE_PRODUCTION_SONGS
                    }
                  >
                    Live (Minimum: {MIN_LIVE_PRODUCTION_SONGS} Songs)
                  </option>
                  <option value="NOT LIVE">Not Live</option>
                </BootstrapForm.Select>
                <BootstrapForm.Text muted>
                  {(editingChannel.liveSongs ?? 0) < MIN_LIVE_PRODUCTION_SONGS &&
                  editingChannel.status !== 'LIVE'
                    ? `Cannot go Live yet — production schedule has ${editingChannel.liveSongs ?? 0} of ${MIN_LIVE_PRODUCTION_SONGS} required songs.`
                    : `Live requires at least ${MIN_LIVE_PRODUCTION_SONGS} songs in the production schedule (currently ${editingChannel.liveSongs ?? 0}).`}
                </BootstrapForm.Text>
              </BootstrapForm.Group>

              <BootstrapForm.Group className="mb-3">
                <BootstrapForm.Label>Music Directors</BootstrapForm.Label>
                <div style={{
                  border: '1px solid #ced4da',
                  borderRadius: '0.375rem',
                  padding: '12px',
                  maxHeight: '200px',
                  overflowY: 'auto',
                  backgroundColor: '#f8f9fa'
                }}>
                  {musicDirectorCandidates.map((director) => (
                        <div key={director.id} style={{ marginBottom: '8px', display: 'flex', alignItems: 'center' }}>
                          <input
                            type="checkbox"
                            id={`edit-${director.id}`}
                            checked={editingDirectorSubs.includes(director.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                handleMusicDirectorsChange([...editingDirectorSubs, director.id]);
                              } else {
                                handleMusicDirectorsChange(editingDirectorSubs.filter(id => id !== director.id));
                              }
                            }}
                            style={{ marginRight: '0px' }}
                          />
                          <label htmlFor={`edit-${director.id}`} style={{ margin: 0, marginLeft: '8px', cursor: 'pointer' }}>
                            {director.name} - {director.role}
                          </label>
                        </div>
                      ))}
                </div>
                <div style={{ marginTop: 8 }}>
                  <BootstrapForm.Text className="text-muted">
                    Select one or more music directors for this channel
                  </BootstrapForm.Text>
                  <br />
                  <BootstrapForm.Text className="text-muted">
                    Leave empty if no music directors are assigned to this channel
                  </BootstrapForm.Text>
                </div>
                {editingDirectorSubs.length > 0 && (
                  <div style={{ marginTop: 8 }}>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => handleMusicDirectorsChange([])}
                      style={{ fontSize: 12, color: '#000000' }}
                    >
                      Clear Selection
                    </Button>
                  </div>
                )}
              </BootstrapForm.Group>

              <BootstrapForm.Group className="mb-3">
                <BootstrapForm.Label>Order *</BootstrapForm.Label>
                <BootstrapForm.Control
                  type="text"
                  placeholder="Enter channel order"
                  value={editingChannel.order}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '' || /^\d+$/.test(value)) {
                      handleEditInputChange('order', value);
                    }
                  }}
                  required
                />
              </BootstrapForm.Group>
            </BootstrapForm>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => {
            setShowEditModal(false);
            setEditingChannel(null);
            setEditingDirectorSubs([]);
          }}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleUpdateChannel}
            disabled={!editingChannel?.name.trim() || updateState.isLoading || setMdState.isLoading}
            style={{ background: '#e21e26', border: 'none' }}
          >
            {updateState.isLoading || setMdState.isLoading ? 'Saving…' : 'Update Channel'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default withAuthenticationRequired(ChannelAdmin);
